import {
  addPricingMappingCodeField,
  getDefaultCouponState,
  handleCouponErrMsg,
  isFromMarketingCampaign,
  getAppliedCouponInfo,
  getEffectiveCouponCode,
  canReapplyCouponInMidTerm,
  shouldAddMidTermFeature,
} from '@/views/config/coupon/helper/utils';
import {
  couponDiscountTypeMap,
  couponFetchStatus,
  couponFormModeMap,
  couponSourceTypeMap,
  couponTypes,
  FLOW_TYPE_KEY,
  PLAN_TYPE_KEY,
} from '@/views/config/coupon/helper/constant';
import { getMultiCouponPlanList, validateCoupon, verifyCoupon } from '@/apis/coupon';
import _, { filter, find, get, omitBy, some, values } from 'lodash';
import { dateTimeFormatWithClientZone, getEarliestTime } from '@/utils/dateformat';
import { ALLOWED_PAID_PLAN_IN_BUYTRIAL, AUDIO_SUB_TYPES, ENUM_ZUORA_COUPON_TYPE, planTypes } from '@/common/constants';
import { isEmptyVal } from '@/utils';
import {
  convertCampaignCouponResult2State,
  convertPartnerReferCouponResult2State,
  convertSubscriptionResult2State,
  convertVerifyCouponResult2State,
} from '@/views/config/coupon/helper/result-to-state';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';
import { CouponErrorHandler } from '@/views/config/coupon/helper/error-handler';
import { logger } from '@/utils/log';
import { parseTncMessage } from '@/views/config/coupon/helper/parse-tnc';
import { computeDiscountWithAmountMap, computedNextBillAmount } from '@/views/config/coupon/helper/compute';
import { plansof } from '@/utils/chain';
import { checkPlanDownSellForEdit } from '@/common/service/validate/downsell';
import { checkCurrentIsEditA2MCases } from '@/views/config/coupon/helper/validator';
import { SupportCouponModeMap } from '@/views/config/coupon/component/support-coupon/constant';
import { shoppingCartPlanInitReady } from '@/utils/ready';
import { couponParamsGeneratorInstance } from '@/views/config/coupon/helper/generate-params';
import { couponHelper } from '@/views/config/coupon/helper/get';
import { translation } from '@/plugins/i18n';
import { showIneligibleDialogForFreeTrial } from '@/common/service/free-trial';

const handlerCouponError = (couponResult = {}, planList = []) => {
  let { errorMessage = '' } = couponResult || {};

  if (isEmptyVal(errorMessage)) {
    const couponErrorHandler = new CouponErrorHandler(couponResult, planList);
    return couponErrorHandler.getErrorMsgWithErrorCode();
  }

  return errorMessage;
};

export const COUPON_ACTION_TYPES = {
  verifyCoupon: 'verify/coupon',
  showTCMessage: 'show/coupon/tnc/message',
  removeCoupon: 'remove/coupon',
  preCheckCoupon: 'pre/check/coupon',
  initCouponState: 'init/coupon/state',
};

const state = {
  couponState: getDefaultCouponState(),
  couponMessage: '',
  verifyCouponStatus: couponFetchStatus.idle,
  couponFormMode: couponFormModeMap.INIT,
  multiCouponPlanList: [],
  supportCouponState: { // This is a special coupon type that applies to all discount rate plans defined in zuora and does not distinguish between processes.
    discountPercentage: window.newBillingConfig?.supportCoupon?.discount,
    couponCode: window.newBillingConfig?.supportCoupon?.instanceId,
    couponDisplayMode: SupportCouponModeMap.unset,
    unSupportedCouponCase: false,
  },
  appliedCouponInfo: null, // Store applied coupon info with midTermFeature flag (paid accounts only)
};

const getters = {
  supportCouponState: (state) => state.supportCouponState,
  supportCouponCode: (state) => state.supportCouponState.couponCode,
  couponSubscriptionLevelAction: (_, getters) => getters.couponState?.subscriptionLevelAction,
  appliedCouponInfo: (state) => state.appliedCouponInfo,
  canReapplyCouponInMidTerm: () => {
    // IMPORTANT: Directly check FTL data instead of relying on state
    // This avoids timing issues during initialization where state.appliedCouponInfo is still null
    // ONLY applies to paid accounts with BOTH conditions:
    // 1. appliedCouponInfo.midTermFeature === true (account has mid-term feature flag)
    // 2. appliedCouponInfo has valid coupon code (instanceId or couponCode exists)
    // Note: This is NOT a special "mid-term coupon" - it's a regular marketing coupon
    // that can be re-verified in mid-contract scenarios
    // Priority: instanceId > couponCode (instanceId is distributed instance generated from couponCode)
    const appliedCouponInfo = getAppliedCouponInfo();
    return canReapplyCouponInMidTerm(appliedCouponInfo);
  },
  useSpecialSupportCoupon: (state, getters) => {
    const useSupportCoupon = !!getters.supportCouponCode;

    // do not have support coupon in current account
    if (!useSupportCoupon) return false;

    // only for edit flow with support coupon in phase one
    if (![flowTypes.edit].includes(pageConfig?.type)) {
      logger.info('support coupon only for edit flow with support coupon in phase one!');
      return false;
    }

    // for some ineligible cases with support coupon
    if (isFromMarketingCampaign()) {
      logger.info('support coupon ineligible for campaign cases!');
      return false;
    }

    return true;
  },
  haveVerifiedSupportCoupon: (state, getters) => {
    return getters.useSpecialSupportCoupon && getters.supportCouponState.couponDisplayMode === SupportCouponModeMap.form;
  },
  couponFingerPrint: (state, getters) => {
    if (getters.useSpecialSupportCoupon) {
      return getters.couponState.couponCode;
    }

    return '';
  },
  couponState: (state) => state.couponState,
  pendingCouponDiscountAmount: (state, getters) => {
    if (getters.couponState?.isPendingCoupon) {
      return getters.discountWithAmountMap?.totalDiscount ?? 0;
    }
    return 0;
  },
  couponDiscountPlanMap: (state, getters) => {
    return getters.discountWithAmountMap?.discountPlans || {};
  },
  couponDiscountPlansList: (state, getters) => {
    return Object.values(getters.couponDiscountPlanMap);
  },
  couponDiscountPercentageValue:(state, getters) => {
    const isPercentageDiscountForCoupon = couponHelper.isPercentageDiscountForCoupon(getters.discountWithAmountMap);
    return isPercentageDiscountForCoupon ? getters.couponDiscountPlansList?.[0]?.discount : 0;
  },
  couponMessage: (state) => state.couponMessage,
  verifyCouponStatus: (state) => state.verifyCouponStatus,
  verifyCouponStatusLoading: (state, getters) => {
    return _.some([state.verifyCouponStatus, ...getters.allPromotionInfoStatusList], (status) => status === couponFetchStatus.pending);
  },
  timebaseEarlyExpireDate: (_, getters) => {
    const { couponType, couponSourceType } = getters.couponState || {};

    if (couponType === couponTypes.MARKETING || couponSourceType === couponSourceTypeMap.threshold) {
      const discountPlans = getters.discountWithAmountMap.discountPlans;
      return getEarliestTime(filter(values(discountPlans), (plan) => !!plan.discountAmount));
    }

    const timeBase = getters.couponState.timeBase;
    const selectedPlan = getters.getSelectedPlansMap;

    return getEarliestTime(filter(timeBase, (item) => {
      return isEmptyVal(selectedPlan[item.plan_code]) === false;
    }));
  },
  tncCouponEarlyExpireDate: (_, getters) => {
    return dateTimeFormatWithClientZone(getters.timebaseEarlyExpireDate);
  },
  // next billing amount, need compare current plan's promotion expire date and next billing cycle date
  nextBillAmount: () => computedNextBillAmount(),
  tncMessage: () => parseTncMessage(),
  // Compute all collections of products with discounts
  discountWithAmountMap: () => computeDiscountWithAmountMap(),
  getEffectSelectedPlansMap: (state, getters) => {
    const selectedPlansMap = getters.getSelectedPlansMap;
    const inrAudioPlan = find(getters.getTypeGroupSelectedPlans[planTypes.TOLLFREE], (item) => {
      return item.category === AUDIO_SUB_TYPES.INDIA_AUDIO;
    });

    if (inrAudioPlan) {
      // filter biz + inr audio plan, avoid calculate discount with threshold coupon
      return omitBy(selectedPlansMap, (item) => {
        return item.type === planTypes.ZOOM_MEETING && item.category === ZOOM_MEETING_SUB_TYPES.BIZ;
      });
    } else {
      return selectedPlansMap;
    }
  },
  couponFormMode: (state) => state.couponFormMode,
  forNewSubscriptionFlow: (state, getters) => {
    const isTrialFlowWithSubscription = pageConfig?.type === flowTypes.buyTrial &&
      plansof(getters.getSelectedPlans).type(...ALLOWED_PAID_PLAN_IN_BUYTRIAL).not().freeTrial().has()
    return [flowTypes.basic2pro, flowTypes.buyNow].includes(pageConfig?.type) || isTrialFlowWithSubscription
  },
  fromCampaignLink: (state, getters) => {
    const { couponSourceType, couponType } = state.couponState;

    // for net new Customer or free account
    if (getters.forNewSubscriptionFlow) {
      if ((couponType === couponTypes.MARKETING
        && ![couponSourceTypeMap.preload, couponSourceTypeMap.threshold].includes(couponSourceType))
        || couponSourceTypeMap.marketing === couponSourceType) {
        return true;
      }
    } else if (couponType === couponTypes.MARKETING || window?.$context?.is?.fromPromoCodeCampaign) {
      return true;
    }

    return false;
  },
  isFromCancelCampaign: (state) => {
    const { couponType, discountInfo } = state.couponState;

    // OBT-4969: Hide coupon section for save offer from cancellation flow
    if (
      couponType === couponTypes.MARKETING &&
      Object.values(discountInfo).some((i) => i.couponDefType === ENUM_ZUORA_COUPON_TYPE.CANCELLATION)) {
      return true;
    }
    return false;
  },
  isReplaceSubscriptionMarketingCoupon: (state, getters) => {
    return getters.fromCampaignLink && !getters.forNewSubscriptionFlow;
  },
  multiCouponPlanList: (state) => state.multiCouponPlanList,
  isMultipleCoupon: (state, getters) => {
    const existDiscount = some(getters.subscriptions, (p) => p?.discount > 0);
    return existDiscount && getters.fromCampaignLink;
  },
  haveAppliedThresholdCoupon: (state, getters) => {
    return some(getters.subscriptions, (p) => p?.couponDiscountType === couponDiscountTypeMap.thresholdAmount);
  },
  appliedPlans4ThresholdCoupon: (state, getters) => {
    return _.filter(getters.boughtPlans, (p) => p?.couponDiscountType === couponDiscountTypeMap.thresholdAmount);
  },
  mixBuyPlanType4FlowType: (state, getters) => { /// key -> flow Type, value -> planType list
    const marketingCoupon = pageConfig?.partnerReferral?.coupon || pageConfig?.marketingCoupon;

    if (isEmptyVal(marketingCoupon)) return {};

    const { discountInfo, discountInfoExtension } = marketingCoupon;
    const allDiscountInfo = {...discountInfo, ...discountInfoExtension };
    const groupByPlanCodePlanMap = getters.groupByPlanCodePlanMap;

    return _.chain(allDiscountInfo)
      .keys()
      .map((planCode) => {
        const plan = _.get(groupByPlanCodePlanMap, [planCode]);
        const discountInfo = _.get(allDiscountInfo, [planCode]);
        return plan ? {...plan, ...discountInfo } : undefined;
      })
      .filter(Boolean)
      .groupBy(FLOW_TYPE_KEY)
      .mapValues((planList) => {
        return _.chain(planList)
          .map(PLAN_TYPE_KEY)
          .uniq()
          .value();
      })
      .value();
  },
  // for mix buy flow, exist edit plan case (eg: edit zoom one pro/biz + add addon plan)
  mixBuyFlowContainsEditCase: (state, getters) => {
    if (pageConfig?.type !== flowTypes.mixBuy) return false;
    return get(getters.mixBuyPlanType4FlowType, [flowTypes.edit], []).length > 0;
  },
  accountWithCouponDiscount: (state, getters) => {
    // account apply a coupon in shopping cart
    if (getters.discountWithAmountMap?.totalDiscount > 0) {
      return true;
    }

    // account have forever coupon
    if (getters.basicConfig?.hasAccountDiscount) {
      return true;
    }

    // account is in multiple coupon case
    if (getters.isMultipleCoupon) {
      return true;
    }

    // account have threshold coupon
    if (getters.haveAppliedThresholdCoupon) {
      return true;
    }
  },
};

const mutations = {
  setSupportCouponState(state, data = {}) {
    Object.keys(data).forEach((key) => {
      state.supportCouponState[key] = data[key];
    });
  },
  setCouponState(state, couponState) {
    state.couponState = couponState;
  },
  setCouponMessage(state, message) {
    state.couponMessage = message;
  },
  resetCouponState(state) {
    state.couponState = getDefaultCouponState();
    state.verifyCouponStatus = couponFetchStatus.idle;
  },
  setVerifyCouponStatus(state, verifyCouponStatus) {
    state.verifyCouponStatus = verifyCouponStatus;
  },
  setCouponFormMode(state, data) {
    state.couponFormMode = data;
  },
  setMultiCouponPlanList(state, data = []) {
    state.multiCouponPlanList = data;
  },
  setAppliedCouponInfo(state, appliedCouponInfo) {
    state.appliedCouponInfo = appliedCouponInfo;
  },
};

const actions = {
  async checkUnsupportedCouponCase({ commit, getters }) {
    if (window.$context.is.edit && window.$isEnabled("blockSupportCouponWithA2MAndDownsell")) {
      try {
        await checkPlanDownSellForEdit(true);
        commit('setSupportCouponState', { unSupportedCouponCase: false });
      } catch (e) {
        commit('setSupportCouponState', { unSupportedCouponCase: true });
      }

      if (!getters.supportCouponState?.unSupportedCouponCase) {
        commit('setSupportCouponState', { unSupportedCouponCase: checkCurrentIsEditA2MCases() });
      }
    }
  },
  async verifySupportCoupon({ dispatch, getters }, payload) {
    // block downsell and A2M case (just for frontend block)
    await dispatch('checkUnsupportedCouponCase');
    const { unSupportedCouponCase } = getters.supportCouponState;
    const unSupportedErrorCode = '379999';

    if (unSupportedCouponCase) {
      const errorMessage = handlerCouponError({ errorCode: unSupportedErrorCode });
      await dispatch('setCouponFailState', { errorMessage, errorCode: unSupportedErrorCode });
    } else {
      await dispatch(COUPON_ACTION_TYPES.verifyCoupon, payload);
    }
  },
  setCouponFailState({ commit }, payload = {}) {
    commit('setCouponState', handleCouponErrMsg(payload));
    commit('setVerifyCouponStatus', couponFetchStatus.failure);
  },
  async [COUPON_ACTION_TYPES.verifyCoupon]({ getters, commit, dispatch }, payload = {}) {
    const { couponCode = '' } = payload;

    if (isEmptyVal(couponCode)) return Promise.resolve();

    commit('setVerifyCouponStatus', couponFetchStatus.pending);

    const params = couponParamsGeneratorInstance.generateVerifyCouponParams({
      couponCode,
      midTermFeature: shouldAddMidTermFeature(couponCode, getters.appliedCouponInfo),
    });

    try {
      const result = await verifyCoupon(params);

      commit('setCouponState', convertVerifyCouponResult2State(result, { couponCode }));
      commit('setVerifyCouponStatus', couponFetchStatus.successfully);
    } catch (couponResult) {
      logger.error('verify coupon api', couponResult);
      const errorMessage = handlerCouponError(couponResult, getters.planMapFlat);
      dispatch('setCouponFailState', { errorMessage, errorCode: couponResult?.errorCode });
    }
  },
  async [COUPON_ACTION_TYPES.preCheckCoupon]({ getters, commit, dispatch }, payload = {}) {
    try {
      const { couponCode = '' } = payload;

      if (isEmptyVal(couponCode)) return Promise.resolve();

      commit('setVerifyCouponStatus', couponFetchStatus.pending);

      const params = couponParamsGeneratorInstance.generateValidateCouponParams({ couponCode });

      await validateCoupon(params);

      commit('setVerifyCouponStatus', couponFetchStatus.successfully);
    } catch (couponResult) {
      logger.error('validate coupon api', couponResult);
      const errorMessage = handlerCouponError(couponResult, getters.planMapFlat);
      const errorResult = { errorMessage, errorCode: couponResult?.errorCode };

      // for Net new customer or free account, when the current coupon valid fail, should remove coupon state, add error message and set fail status
      if (getters.forNewSubscriptionFlow) {
        dispatch('setCouponFailState', errorResult);
      } else {
        // for edit flow/add addon flow, Need to keep coupon related information, just add error message and set fail status
        commit('setCouponState', {...getters.couponState, ...errorResult });
        commit('setVerifyCouponStatus', couponFetchStatus.failure);
      }
    }
  },
  async updateReplaceSubscriptionList({ getters, commit }) {
    const couponState = getters.couponState;
    // just for edit base/addon && upsell addon cases
    // when trigger replace subscription with marketing coupon
    if ([flowTypes.edit, flowTypes.add2cart].includes(pageConfig.type) && couponState?.fromPreloadLink) {
      try {
        const { result = [] } = await getMultiCouponPlanList();
        commit("setMultiCouponPlanList", addPricingMappingCodeField(result));
      } catch (e) {
        logger.error("api fetch error", e);
      }
    }
  },
  async doCampaignAction({ commit, dispatch }) {
    commit('setCouponState', convertCampaignCouponResult2State(pageConfig?.marketingCoupon));
    commit('setVerifyCouponStatus', couponFetchStatus.successfully);
    dispatch('updateReplaceSubscriptionList');
  },
  async doPreloadCartIdAction({ dispatch }) {
    const { orderData } = pageConfig?.preloadCartInfo || {};
    // The coupon parameter has a higher priority than the one in cartId.
    const couponCode = pageConfig?.coupon || orderData?.coupon?.code;
    dispatch(COUPON_ACTION_TYPES.verifyCoupon, { couponCode });
  },
  async doPartnerReferAction({ commit }) {
    const { coupon, campaignCode } = pageConfig.partnerReferral || {};
    const { trialCampaignInfo } = pageConfig;
    // if campaignCode exists and trialCampaignInfo exists, set isPendingCoupon to true (with free trial flow)
    const isPendingCoupon = campaignCode && !isEmptyVal(trialCampaignInfo);
    commit('setCouponState', convertPartnerReferCouponResult2State({
      ...coupon,
      isPendingCoupon,
    }));
    commit('setVerifyCouponStatus', couponFetchStatus.successfully);
  },
  async doCouponSessionAction({ getters, dispatch }) {
    dispatch(COUPON_ACTION_TYPES.verifyCoupon, { couponCode: getters.couponState?.couponCode });
  },
  async doSpecialSupportCouponAction({ getters, dispatch, commit }) {
    commit('setSupportCouponState', { couponDisplayMode: SupportCouponModeMap.form });
    // This is mainly to solve the problem that the initialization of some products is not performed in the initShoppingCart method, but in the current product component.
    shoppingCartPlanInitReady.ready(() => {
      dispatch('verifySupportCoupon', { couponCode: getters.supportCouponCode });
    });
  },
  async doEditSubscriptionAction({ getters, commit }) {
    const timeBase = convertSubscriptionResult2State(getters.subscriptions);

    if (timeBase.length) {
      commit('setCouponState', {
        ...getDefaultCouponState(),
        timeBase,
        couponType: couponTypes.TIMEBASE,
      });
    }
  },
  async doTrial2PaidAction({ dispatch }, payload = {}) {
    if(isEmptyVal(payload?.couponCode)) return;
    dispatch(COUPON_ACTION_TYPES.verifyCoupon, { couponCode: payload?.couponCode });
  },
  async doCouponQueryParamAction({ dispatch }) {
    dispatch(COUPON_ACTION_TYPES.verifyCoupon, { couponCode: pageConfig?.coupon });
  },
  async doCouponPersistQueryParamAction({ dispatch, commit }) {
    dispatch(COUPON_ACTION_TYPES.verifyCoupon, { couponCode: pageConfig?.persistCoupon });
    commit('setCouponFormMode', couponFormModeMap.EDIT);
  },
  async [COUPON_ACTION_TYPES.initCouponState]({ dispatch, getters }) {
    // for preload cart info with delta (add quantity with product)
    if (window.$context?.is?.delta) return;

    const { marketingCoupon, preloadCartInfo, partnerReferral, type: flowType } = pageConfig;
    // first check special coupon code, when the purchase link is not from campaign link (marketing campaign/partner etc...)
    if (getters.useSpecialSupportCoupon) {
      logger.info('init support coupon state: for special support coupon with coupon state!');
      return dispatch('doSpecialSupportCouponAction');
    }

    // Priority 2: Mid-term coupon reapplication (mid-contract scenarios with nonprofit discount)
    // Requires BOTH midTermFeature flag AND couponCode to be present
    // Note: This is NOT a special coupon type - regular marketing coupon that can be re-verified
    // TODO by arvin: Handle conflict when both support coupon and mid-term feature exist
    if (getters.canReapplyCouponInMidTerm) {
      logger.info('init coupon state: can reapply coupon in mid-term scenarios');
      return dispatch('doMidTermCouponAction');
    }

    // priority is first check session
    if (getters.forNewSubscriptionFlow && !isEmptyVal(getters.couponState.couponCode)) {
      logger.info("init coupon state: for session store cache with coupon state");
      return dispatch("doCouponSessionAction");
    }

    // persist coupon code with switch browser link
    if (getters.forNewSubscriptionFlow && pageConfig.persistCoupon && isEmptyVal(pageConfig?.couponErrorResult)) {
      logger.info("init coupon state: for persist coupon code with switch url");
      return dispatch("doCouponPersistQueryParamAction");
    }

    if (marketingCoupon) {
      logger.info("init coupon state: for campaign link [/buy/campaign, /billing/campaign, /billing/campaign/cancel]");
      return dispatch('doCampaignAction');
    }

    if (!isEmptyVal(preloadCartInfo) && getters.forNewSubscriptionFlow) {
      logger.info("init coupon state: for share shopping cart with cartId");
      return dispatch('doPreloadCartIdAction');
    }

    if (partnerReferral?.coupon) {
      logger.info("init coupon state: for partner refer with coupon case");
      return dispatch('doPartnerReferAction');
    }

    // when page init by coupon validation with backend return couponErrorResult, don't need call coupon api
    if (getters.forNewSubscriptionFlow && pageConfig?.coupon && isEmptyVal(pageConfig?.couponErrorResult)) {
      logger.info("init coupon state: for query with coupon parameter");
      return dispatch("doCouponQueryParamAction");
    }

    if ([flowTypes.edit, flowTypes.add2cart].includes(flowType)) {
      logger.info(`init coupon state: for ${flowType} subscription with origin coupon`);
      return dispatch("doEditSubscriptionAction");
    }
  },
  /**
   * Validate free trial coupon with soldToCountry
   * @param {Object} context - Vuex action context
   * @param {Object} payload - Payload object
   * @param {string} payload.couponCode - Coupon code to validate
   * @param {string} payload.soldToCountry - Sold to country code
   * @returns {Promise} Promise that resolves if validation succeeds or user proceeds without offer
   */
  async validateFreeTrialByCoupon({ getters }, payload = {}) {
    const { couponCode = '', soldToCountry = '' } = payload;

    try {
      const params = couponParamsGeneratorInstance.generateFreeTrialValidateCouponParams({ couponCode, soldToCountry });
      await verifyCoupon(params);
      return Promise.resolve();
    } catch (error) {
      // Parse error message using CouponErrorHandler
      const errorMessage = handlerCouponError(error, getters.selectedPlanListPayLoad);

      return showIneligibleDialogForFreeTrial({
        title: translation('billing.common.can_not_continue'),
        message: errorMessage,
        confirmButtonText: translation('billing.partner.proceed_without_offer'),
        cancelButtonText: translation('billing.common.cancel'),
      }).then(() => {
        window.location.href = '/pricing';
        return Promise.reject();
      });
    }
  },
  /**
   * Handle mid-term feature coupon action (paid accounts only)
   * @param {Object} context - Vuex action context
   */
  async doMidTermCouponAction({ dispatch, commit }) {
    const appliedCouponInfo = getAppliedCouponInfo();
    commit('setAppliedCouponInfo', appliedCouponInfo);

    // Priority: instanceId > couponCode
    // instanceId is the distributed instance generated from couponCode when user first redeems
    // Use instanceId for subsequent purchases to get discount info for other products
    const effectiveCouponCode = getEffectiveCouponCode(appliedCouponInfo);

    shoppingCartPlanInitReady.ready(() => {
      // Reuse existing verifyCoupon action
      dispatch(COUPON_ACTION_TYPES.verifyCoupon, {
        couponCode: effectiveCouponCode
      });
    });
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
